import CTAButton from '@/components/shared/CTAButton'
import Reviews from '@/components/Reviews'
import { Card, CardContent } from '@/components/ui/card'
import {
  IconSquareCheck,
  IconScale,
  IconCurrencyPound,
} from '@tabler/icons-react'

export function ClaimScenarios({ className = '', compact = false }) {
  return (
    <section className={`w-full pt-12 sm:pt-16 ${className}`}>
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="text-foreground mb-4 text-center text-4xl leading-tight font-black sm:mb-6 sm:text-5xl">
          FIND OUT WHETHER YOU MAY BE ENTITLED TO COMPENSATION
        </h2>

        <p className="text-foreground text-center text-lg">
          Great news, the Financial Conduct Authority (FCA) has announced a mass
          redress scheme. It’s estimated that up to{' '}
          <span className="font-raleway-numbers">37%</span>
          <sup style={{ fontSize: '0.7em', verticalAlign: 'super' }}>4</sup> of
          vehicles purchased on finance were mis-sold, including some lenders
          allowing higher interest rates to be charged, so you could have
          overpaid! Millions of people are unknowingly affected.
          <br />
          <br />
          Your household could receive up to{' '}
          <strong>
            £<span className="font-raleway-numbers">2,086</span>
          </strong>
          <sup style={{ fontSize: '0.7em', verticalAlign: 'super' }}>1</sup>.
          That&apos;s because the average household contains{' '}
          <span className="font-raleway-numbers">2</span> adults, our analysis
          identifies an average of{' '}
          <span className="font-raleway-numbers">3.4</span> vehicle finance
          agreements per person, and around{' '}
          <span className="font-raleway-numbers">37%</span> of agreements may be
          affected. With average compensation estimated at £
          <span className="font-raleway-numbers">829</span> per eligible
          agreement, many households could have significant compensation
          opportunities. Be part of a potential payout worth up to{' '}
          <strong>£10 billion</strong>
          {'! '}It&apos;s estimated that around{' '}
          <strong>
            <span className="font-raleway-numbers">12.1</span> million
          </strong>{' '}
          finance agreements were affected, meaning many drivers could be
          entitled to compensation.
          <br />
          <br />
          Car & motor finance claims have been featured in{' '}
          <strong>BBC News</strong>, <strong>ITV</strong>, and{' '}
          <strong>The Telegraph</strong>.
          <br />
          <br />
          Please be aware that you can make a free motor finance mis-selling
          claim directly with your lender, through the FCA redress scheme, or
          via the Financial Ombudsman Service. We can do the work for you, but
          you do not need to use a claims management company or law firm to make
          a claim.
          <br />
          <br />
          <span className="text-lg">
            Please see our Key Information for further details.
          </span>
        </p>

        <div className="mt-8 mb-8 text-center">
          <CTAButton />
        </div>

        {/* Text and Image Section */}
        {!compact && (
          <>
            <div className="mt-16 grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
              {/* Text Content - Left Side */}
              <div className="space-y-6 text-left">
                <h2 className="text-foreground text-3xl font-black sm:text-4xl">
                  You can claim for:
                </h2>

                {/* Bullet Points with Tick Icons */}
                <div className="flex flex-col items-start space-y-4">
                  <div className="flex items-start gap-3">
                    <svg
                      className="mt-0.5 h-6 w-6 flex-shrink-0"
                      style={{ color: 'oklch(75% 0.183 55.934)' }}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <span className="text-foreground text-lg">
                      Multiple new or used vehicles!
                    </span>
                  </div>

                  <div className="flex items-start gap-3">
                    <svg
                      className="mt-0.5 h-6 w-6 flex-shrink-0"
                      style={{ color: 'oklch(75% 0.183 55.934)' }}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <span className="text-foreground text-lg">
                      Motor vehicles financed after{' '}
                      <span className="font-raleway-numbers">2007</span>,
                      whether a car, van, motorbike or campervan.
                    </span>
                  </div>

                  <div className="flex items-start gap-3">
                    <svg
                      className="mt-0.5 h-6 w-6 flex-shrink-0"
                      style={{ color: 'oklch(75% 0.183 55.934)' }}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <span className="text-foreground text-lg">
                      Any hidden commissions or high interest rates, you were
                      not aware of.
                    </span>
                  </div>
                </div>

                {/* Paragraph */}
                <p className="text-foreground text-lg leading-relaxed">
                  Our highly experienced claims management team is here to help.
                </p>
              </div>

              {/* Image - Right Side */}
              <div className="lg:order-last">
                <img
                  src="/Image/car-finance-claim-claims1.webp"
                  alt="Car finance claim - Claims1 services"
                  className="h-auto w-full rounded-lg shadow-lg"
                />
              </div>
            </div>

            {/* Centered CTA Button */}
            <div className="mt-8 mb-8 text-center">
              <CTAButton />
            </div>

            {/* SIT BACK WHILE WE DO THE HARD WORK Section */}
            <div className="mt-16">
              <h2 className="text-foreground mb-4 text-center text-4xl font-black sm:mb-6 sm:text-5xl">
                SIT BACK WHILE WE DO THE HARD WORK
              </h2>

              <p className="text-foreground mb-8 text-center text-lg sm:mb-12">
                It&apos;s a simple <strong>3 step</strong> claim process:
              </p>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:gap-6">
                <Card className="border-foreground group rounded-2xl border-2 bg-white transition-all duration-300 hover:scale-105 hover:shadow-lg">
                  <CardContent className="p-4 text-center lg:p-6">
                    <div className="mb-4 flex justify-center">
                      <IconSquareCheck size={48} className="text-orange-400" />
                    </div>
                    <h3 className="text-foreground mb-4 text-lg font-bold">
                      We locate your agreements
                    </h3>
                    <p className="text-foreground text-sm leading-relaxed">
                      Our system links with major credit reference agencies and
                      vehicle registration databases enabling us to find all car
                      finance agreements connected to you, even if you&apos;ve
                      changed your name or moved address. This includes
                      agreements dating back to{' '}
                      <span className="font-raleway-numbers">2007</span> which
                      are often hard for individuals to trace.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-foreground group rounded-2xl border-2 bg-white transition-all duration-300 hover:scale-105 hover:shadow-lg">
                  <CardContent className="p-4 text-center lg:p-6">
                    <div className="mb-4 flex justify-center">
                      <IconScale size={48} className="text-orange-400" />
                    </div>
                    <h3 className="text-foreground mb-4 text-lg font-bold">
                      We handle everything
                    </h3>
                    <p className="text-foreground text-sm leading-relaxed">
                      Your claim is handed to our partner law firm who ensure
                      your lender provides exact information related to how much
                      you were overcharged on your vehicle finance.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-foreground group rounded-2xl border-2 bg-white transition-all duration-300 hover:scale-105 hover:shadow-lg">
                  <CardContent className="p-4 text-center lg:p-6">
                    <div className="mb-4 flex justify-center">
                      <IconCurrencyPound
                        size={48}
                        className="text-orange-400"
                      />
                    </div>
                    <h3 className="text-foreground mb-4 text-lg font-bold">
                      The heavy lifting starts
                    </h3>
                    <p className="text-foreground text-sm leading-relaxed">
                      Once all of the evidence related to your claims is
                      collected, the law firm will negotiate directly with your
                      lenders and work to recover your compensation, all on a No
                      win, no fee³ basis. Meanwhile, you can sit back and relax.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-8 mb-8 text-center">
                <CTAButton />
              </div>
            </div>
          </>
        )}
      </div>
      {!compact && (
        <div className="mt-12">
          <Reviews />
        </div>
      )}
    </section>
  )
}
export default ClaimScenarios
